import { useMutation } from '@tanstack/react-query'
import axios from 'axios'
import React from 'react'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { PostResetPasswordToastMessage } from 'src/app/Toast/Toast'
import { useBackgroundDispatch } from 'src/hooks'
import { translateNode } from 'src/localization/translateNode'
import { connectedPush } from 'src/navigation/Lnk'
import { signinUrl } from 'src/navigation/urls'
import { getRequestConfig, RequestConfig } from 'src/queries/requestConfig'
import {
  POST_COGNITO_RESET_PASSWORD_INIT_FAILURE,
  POST_COGNITO_RESET_PASSWORD_INIT_INIT,
  POST_COGNITO_RESET_PASSWORD_INIT_SUCCESS,
  POST_RESET_PASSWORD_FAILURE,
  POST_RESET_PASSWORD_INIT,
  POST_RESET_PASSWORD_SUCCESS,
} from 'src/redux/actions/auth.actions'
import { CognitoResetPasswordConfirmRequest } from 'src/requests/cognitoResetPasswordConfirm.request'
import { CognitoResetPasswordInitRequest } from 'src/requests/cognitoResetPasswordInit.request'
import {
  BackendErrorPayload,
  getBackendErrorPayload,
} from 'src/utils/error.utils'

// `response` is optional: Axios network/abort rejections reject with no
// `.response`, so consumers must guard (`error?.response?.data`) — a required
// shape would let downstream reads deref undefined at runtime.
type BackendErrorResponse = { response?: { data: BackendErrorPayload<string> } }

type UseResetPasswordVariables = {
  email: string
  openToast?: boolean
}

/** Auth0 / legacy forgot-password email. Replaces `postResetPassword`. */
export const useResetPassword = () => {
  const dispatch = useBackgroundDispatch()

  return useMutation({
    mutationFn: ({
      email,
      openToast = false,
    }: UseResetPasswordVariables): Promise<void> => {
      dispatch({ type: POST_RESET_PASSWORD_INIT })
      return axios
        .post('/forgot', { email }, getRequestConfig(RequestConfig.Unauthed))
        .then(
          (response) => {
            if (openToast) {
              dispatch(
                createSetIsToastOpen(true, {
                  toastMessage: <PostResetPasswordToastMessage email={email} />,
                }),
              )
            }
            dispatch({
              type: POST_RESET_PASSWORD_SUCCESS,
              payload: response.data,
            })
          },
          (err: BackendErrorResponse) => {
            dispatch({
              type: POST_RESET_PASSWORD_FAILURE,
              payload: getBackendErrorPayload(err),
            })
          },
        )
    },
  })
}

type UseCognitoResetPasswordInitVariables = {
  body: Partial<CognitoResetPasswordInitRequest>
  newCode?: boolean
}

/** Cognito reset-password code request. Replaces `postCognitoResetPasswordInit`. */
export const useCognitoResetPasswordInit = () => {
  const dispatch = useBackgroundDispatch()

  return useMutation({
    mutationFn: ({
      body,
      newCode,
    }: UseCognitoResetPasswordInitVariables): Promise<void> => {
      dispatch({ type: POST_COGNITO_RESET_PASSWORD_INIT_INIT })
      return axios
        .post(
          '/reset-password/init',
          body,
          getRequestConfig(RequestConfig.Unauthed),
        )
        .then(
          (response) => {
            dispatch({
              type: POST_COGNITO_RESET_PASSWORD_INIT_SUCCESS,
              payload: response.data,
            })
            if (newCode) {
              dispatch(
                createSetIsToastOpen(true, {
                  toastMessage: translateNode('New code sent!'),
                }),
              )
            }
          },
          (err: BackendErrorResponse) => {
            dispatch({
              type: POST_COGNITO_RESET_PASSWORD_INIT_FAILURE,
              payload: getBackendErrorPayload(err),
            })
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: translateNode('Code could not be sent'),
                failureColorScheme: true,
              }),
            )
          },
        )
    },
  })
}

/**
 * Cognito reset-password confirm (code + new password). Replaces
 * `postCognitoResetPasswordConfirm`.
 */
export const useCognitoResetPasswordConfirm = () => {
  const dispatch = useBackgroundDispatch()

  // Loading and error now live on the mutation (isPending / error); the form
  // reads them directly instead of via the auth Redux slice. The rejection is
  // deliberately left unhandled so React Query captures it as `error`
  // (BackendErrorResponse), which the form maps with parseBackendErrors — the
  // exact shape the removed POST_COGNITO_RESET_PASSWORD_CONFIRM_FAILURE case
  // computed.
  return useMutation<
    void,
    BackendErrorResponse,
    Partial<CognitoResetPasswordConfirmRequest>
  >({
    mutationFn: async (
      body: Partial<CognitoResetPasswordConfirmRequest>,
    ): Promise<void> => {
      await axios.post(
        '/reset-password/confirm',
        body,
        getRequestConfig(RequestConfig.Unauthed),
      )
    },
    // Redirect + toast sit on `useMutation`, not the `mutate()` call site that
    // `src/queries/AGENTS.md` prefers for them: the redirect to /signin is this
    // mutation's contract rather than the form's, and a call-site callback is
    // skipped if the form unmounts mid-flight — which would change the password
    // and strand the user on the form.
    onSuccess: (): void => {
      dispatch(connectedPush(signinUrl()))
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: translateNode(
            'Success! Your password has been changed.',
          ),
        }),
      )
    },
  })
}
